import React from 'react';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import eagleEyeSprite from '@rsos/components/capstone/common/Map/assets/eagle_eye_sprite_2x.png';
import {
  hidePopover,
  showPopover,
} from '@rsos/components/capstone/common/Map/useDeckGL/MapPopover/mapPopoverSlice';
import { default as IconLayer } from '@rsos/components/capstone/common/Map/useDeckGL/RSOSLayers/RSOSIconLayer';
import {
  trackMarkerClick,
  trackMarkerHover,
} from '@rsos/components/capstone/common/Map/useDeckGL/spatialTracking';
import layers from '@rsos/components/capstone/common/Map/useDeckGL/storeLayers';
import {
  EAGLE_EYE_MAP_CARD,
  setCardsToRender,
  setMapCardData,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { resetEagleEye, setCurrentSelectedCallerID } from './eagleEyeSlice';
import {
  ICON_MAPPING,
  MARKER_SIZES,
  LAYERS,
  selectLatestDataset,
} from './helpers';

const useEagleEyeGL = ({
  activeMarkerId,
  overlay,
  onClick,
  onHover,
  isMapIconDisabled,
}) => {
  const dispatch = useDispatch();
  const datasetObj = useSelector(selectLatestDataset);
  const { topic, dataset } = datasetObj;
  const visible = useSelector(state => state.eagleEye.visible);
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  useEffect(() => {
    layers.deleteLayer(LAYERS.EAGLE_EYE);
    overlay?.setLayers([...layers.getAll()]);
    dispatch(resetEagleEye());
    if (selectedCallerID) {
      dispatch(setCurrentSelectedCallerID(selectedCallerID));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedCallerID]);

  // Draw markers
  useEffect(
    () => {
      if (overlay && dataset && selectedCallerID == topic) {
        layers.save(
          new IconLayer({
            id: LAYERS.EAGLE_EYE,
            data: Object.values(dataset),
            getPosition: d => {
              return d?.shape?.features[0]?.geometry?.coordinates;
            },
            pickable: !isMapIconDisabled,
            autoHighlight: true,
            highlightColor: [255, 255, 255, 60],
            iconAtlas: eagleEyeSprite,
            iconMapping: ICON_MAPPING,
            sizeUnits: 'meters',
            ...MARKER_SIZES,
            getIcon: d => {
              if (d.external_id === activeMarkerId) {
                return 'cameraSelected';
              }
              return 'cameraDefault';
            },

            onHover: info => {
              onHover(info, LAYERS.EAGLE_EYE);
            },

            onClick: info => {
              onClick(info, LAYERS.EAGLE_EYE);
            },
            visible,
            updateTriggers: {
              getIcon: [activeMarkerId],
            },
          }),
        );

        overlay.setLayers([...layers.getAll()]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      overlay,
      dataset,
      activeMarkerId,
      visible,
      onClick,
      onHover,
      isMapIconDisabled,
    ],
  );
};

export const EagleEyeGL = React.memo(({ overlay, isMapIconDisabled }) => {
  const dispatch = useDispatch();
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [hoveredMarkerInfo, setHoveredMarkerInfo] = useState(null);

  const layerData = useSelector(state => state.schemas.byId[LAYERS.EAGLE_EYE]);

  const openedMarkerId = useSelector(state => {
    return state.mapCard?.mapCardData?.spatialObject?.external_id;
  });

  // Keep track of the map card, when it closes, clear the active marker.
  useEffect(() => {
    if (!openedMarkerId) {
      setActiveMarkerId(null);
    }
  }, [openedMarkerId]);

  // Show popover when hovering over a marker or when position changes.
  useEffect(() => {
    if (hoveredMarkerInfo) {
      // default to the original hover object
      let updatedSpatialObject = hoveredMarkerInfo.object;

      dispatch(
        showPopover({
          spatialLayerKey: hoveredMarkerInfo.spatialLayerKey,
          spatialLayer: layerData,
          spatialObject: updatedSpatialObject,
        }),
      );
    }
  }, [dispatch, hoveredMarkerInfo, layerData]);

  const handleMarkerClick = (info, spatialLayerKey) => {
    if (info?.object) {
      setActiveMarkerId(info?.object?.external_id);
      dispatch(
        setMapCardData({
          layerData,
          spatialObject: info?.object,
        }),
      );
      dispatch(setCardsToRender(EAGLE_EYE_MAP_CARD));

      trackMarkerClick(spatialLayerKey, {
        markerId: info?.object?.external_id,
        cameraId: info?.object?.attributes?.camera_information?.deviceId,
      });
    }
  };

  const handleMarkerHover = (info, spatialLayerKey) => {
    if (info?.object) {
      overlay.setCursorPointer();
      setHoveredMarkerInfo({
        ...info,
        spatialLayerKey,
      });
      trackMarkerHover(spatialLayerKey, {
        markerId: info?.object?.external_id,
        cameraId: info?.object?.attributes?.camera_information?.deviceId,
      });
    } else {
      overlay.setCursorDefault();
      setHoveredMarkerInfo(null);
      dispatch(hidePopover());
    }
  };

  useEagleEyeGL({
    activeMarkerId,
    overlay,
    onClick: handleMarkerClick,
    onHover: handleMarkerHover,
    isMapIconDisabled,
  });

  return <></>;
});

export default useEagleEyeGL;
